import { Controller } from "@hotwired/stimulus"

const DEFAULT_AUTO_CLOSE_MS = 500

export default class extends Controller {
	static targets = ["control", "switcher", "button", "icon"]
	static values = {
		activeValue: String,
		activePanel: String,
	}

	initialize() {
		this.switcherOpen = false
		this.closeTimeout = null
	}

	connect() {
		if (!this.hasActiveValueValue) this.activeValueValue = this.buttonTargets[0]?.dataset.value || ""
		if (!this.hasActivePanelValue) this.activePanelValue = "chart"

		this.applyViewState()
	}

	disconnect() {
		this.cancelAutoClose()
	}

	toggle() {
		const wasChartHidden = this.activePanelValue !== "chart"

		this.cancelAutoClose()
		this.dispatch("activate-chart")
		this.activePanelValue = "chart"
		this.switcherOpen = wasChartHidden ? false : !this.switcherOpen
		this.applyViewState()
	}

	close(event) {
		if (!this.switcherOpen) return
		if (event?.target && this.hasControlTarget && this.controlTarget.contains(event.target)) return

		this.cancelAutoClose()
		this.switcherOpen = false
		this.applyViewState()
		this.dispatch("close")
	}

	selectValue({ params }) {
		this.activePanelValue = "chart"
		this.activeValueValue = params.value
		this.switcherOpen = true
		this.applyViewState()
		this.dispatch("change", { detail: { value: this.activeValueValue } })
		this.scheduleAutoClose()
	}

	sync({ detail }) {
		if (!detail) return

		if (detail.activeValue) this.activeValueValue = detail.activeValue
		if (detail.activePanel) this.activePanelValue = detail.activePanel

		if (this.activePanelValue !== "chart") {
			this.cancelAutoClose()
			this.switcherOpen = false
		}

		this.applyViewState()
	}

	scheduleAutoClose() {
		this.cancelAutoClose()
		this.closeTimeout = setTimeout(() => {
			this.closeTimeout = null
			this.switcherOpen = false
			this.applyViewState()
			this.dispatch("close")
		}, DEFAULT_AUTO_CLOSE_MS)
	}

	cancelAutoClose() {
		if (this.closeTimeout == null) return

		clearTimeout(this.closeTimeout)
		this.closeTimeout = null
	}

	// Leitet den internen UI-State in den DOM ab: geoeffnet/geschlossen, aktive Darstellung,
	// Thumb-Position sowie aktive/ARIA-Markierung der Optionen.
	applyViewState() {
		const activeIndex = this.buttonTargets.findIndex((button) => button.dataset.value === this.activeValueValue)
		const normalizedIndex = activeIndex >= 0 ? activeIndex : 0

		// Äußeren Switcher-Wrapper auf den aktuellen Open-/Active-State und die Thumb-Position syncen.
		if (this.hasControlTarget) {
			this.controlTarget.dataset.expanded = String(this.switcherOpen)
			this.controlTarget.dataset.ui = this.activePanelValue === "chart" ? "aktiv" : ""
			this.controlTarget.style.setProperty("--liquid-switcher-index", String(normalizedIndex))
			this.controlTarget.style.setProperty(
				"--liquid-switcher-count",
				String(Math.max(this.buttonTargets.length, 1)),
			)
		}

		// Das sichtbare Chart-Icon separat markieren, damit es in der Tabellenansicht nicht aktiv bleibt.
		if (this.hasIconTarget) this.iconTarget.dataset.ui = this.activePanelValue === "chart" ? "aktiv" : ""

		// Accessibility-State des aufklappbaren Optionsbereichs pflegen.
		if (this.hasSwitcherTarget) this.switcherTarget.ariaHidden = String(!this.switcherOpen)

		// Jede Option visuell und per ARIA als aktiv/inaktiv markieren.
		if (this.hasButtonTarget) {
			this.buttonTargets.forEach((button) => {
				const isActive = button.dataset.value === this.activeValueValue
				button.dataset.active = String(isActive)
				button.ariaPressed = String(isActive)
			})
		}
	}
}
